import { useEffect, useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import { Stethoscope } from 'lucide-react-native';

import { Button, Select, Sheet, TextField, type SelectOption } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { usarInsumo } from '@/services/firebase/ventas';
import { areasQuery } from '@/services/firebase/areas';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { AREAS } from '@/utils/areas';
import type { InsumoInterno } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  insumo: InsumoInterno | null;
}

export function UsarInsumoSheet({ visible, onClose, insumo }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const { data: areasData } = useFirestoreCollection(areasQuery);
  const [cantidad, setCantidad] = useState('1');
  const [destino, setDestino] = useState('uso_interno');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Áreas para "¿dónde se usó?": dinámicas si existen, sino el listado estático.
  const opcionesArea = useMemo<SelectOption[]>(() => {
    const dyn = (areasData as any[]).filter((a) => a.activa !== false);
    const list =
      dyn.length > 0
        ? dyn.map((a) => ({ value: a.slug, label: a.nombre }))
        : AREAS.map((a) => ({ value: a.id, label: a.nombre }));
    return [{ value: 'uso_interno', label: 'Uso interno (general)' }, ...list];
  }, [areasData]);

  useEffect(() => {
    if (!visible) return;
    setCantidad('1');
    setDestino('uso_interno');
    setMotivo('');
    setSaving(false);
    setError(null);
  }, [visible]);

  if (!insumo) return null;

  const cantN = Number(cantidad);
  const stockTras = (insumo.stock ?? 0) - (Number.isFinite(cantN) ? cantN : 0);
  const sinStock = !Number.isFinite(cantN) || cantN <= 0 || cantN > (insumo.stock ?? 0);

  const onSubmit = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    if (sinStock) return setError(`Cantidad inválida o supera el stock (${insumo.stock}).`);
    setSaving(true);
    try {
      const destinoNombre = opcionesArea.find((o) => o.value === destino)?.label ?? 'Uso interno';
      await usarInsumo({
        insumoId: insumo.id,
        cantidad: cantN,
        destino,
        destinoNombre,
        motivo: motivo.trim() || undefined,
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo registrar el uso.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="sm"
      title="Registrar uso"
      subtitle={`${insumo.nombre} · ${insumo.presentacion}`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label="Confirmar uso"
            iconLeft={<Stethoscope size={16} color={colors.white} />}
            onPress={onSubmit}
            loading={saving}
            disabled={sinStock}
          />
        </>
      }
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, padding: 14, borderRadius: radius['2xl'], backgroundColor: colors.crema[50] }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Stock actual
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 26, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {insumo.stock}
          </Text>
        </View>
        <Text style={{ fontSize: 22, color: colors.ink[400] }}>→</Text>
        <View style={{ flex: 1, alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 11, color: stockTras <= 0 ? colors.danger[600] : colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Tras uso
          </Text>
          <Text
            style={{
              fontFamily: typography.display.fontFamily,
              fontSize: 26,
              fontWeight: typography.weights.bold,
              color: stockTras <= 0 ? colors.danger[600] : stockTras <= (insumo.stockMin ?? 0) ? colors.warning[600] : colors.ink[800],
            }}
          >
            {Number.isFinite(stockTras) ? stockTras : '—'}
          </Text>
        </View>
      </View>

      <TextField label="Cantidad" value={cantidad} onChangeText={setCantidad} keyboardType="numeric" />
      <View style={{ gap: 4 }}>
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>¿Dónde se usó?</Text>
        <Select value={destino} onChange={(v) => setDestino((v as string) ?? 'uso_interno')} options={opcionesArea} />
      </View>
      <TextField label="¿Para qué? (opcional)" placeholder="Ej: Consulta gato Mishi · castración" value={motivo} onChangeText={setMotivo} multiline />

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
